import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { AuthContextValue, AuthProviderProps, AuthProviers } from './auth-types';
import { AuthContext } from './context';
import { create, findByWallet, generateJwt } from '@/services/persea-user/persea-user';
import { PerseaUserType } from '@/services/persea-user/persea-user-type';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NotificationPlacement } from 'antd/es/notification/interface';
import { notification, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTheme } from '../theme-context/useThemeContext';
import { getJWTLocalStorage, removeJWTLocalStorage, setXApiKeyLocalStorage } from '@/utils/local-storage/user/local-storage';
import { useReactiveState } from '../buildDinamycState';
import { useBalance } from '@/hook/useBalance/useBalance';
import { amplitudeAddTrack } from '@/utils/amplitud/event-track';
import { isLoggedInMagic, loginEmailMagicLink, loginSocialMagic, logoutMagic, redirectLoginMagic } from '@/services/auth/magic';
import { isLoggedInOauth, loginSocialOath, logoutOath } from '@/services/auth/oauth';

const MESSAGES = {
    LOGIN: 'Iniciando sesión...',
    LOGOUT: 'Cerrando sesión...',
    LOADING: 'Por favor espera...',
    VALIDATE_SESSION: 'Validando sesión...',
    GET_DATA: 'Obteniendo datos...',
    ERROR: 'Ocurrió un error al iniciar sesión',
    ERROR_LOGOUT: 'Error al iniciar sesión.',
    ERROR_VALIDATE_SESSION: 'Error al validar sesión',
}

const LOGIN_FUNCTIONS = {
    MAGIC: {
        login: loginSocialMagic,
        reconnect: redirectLoginMagic,
        isLogged: isLoggedInMagic,
        logout: logoutMagic,
    },
    OAUTH:{
        login: loginSocialOath,
        reconnect: async () =>{},
        isLogged: isLoggedInOauth,
        logout: logoutOath
    }
} as const;

const DOMAIN = import.meta.env.VITE_DOMAIN;
const NETWORK_NAME = import.meta.env.VITE_NETWORK;

export const AuthContextProvider: React.FC<AuthProviderProps> = ({ defaultValues, children }) => {
    const selectedLogin = import.meta.env.VITE_LOGIN_PROVIDER || 'MAGIC';
    const loginFunc = LOGIN_FUNCTIONS[String(selectedLogin)?.toUpperCase() as  keyof typeof LOGIN_FUNCTIONS];
    const { notificationHolderError = true, notificationHolderLoading = true } = defaultValues ?? {};
    const defaultValuesReactive = useReactiveState(defaultValues ?? {}) as AuthContextValue;
    const existDefaultValues = Object.keys(defaultValuesReactive).length > 0;
    const navigate = useNavigate();
    const { theme } = useTheme();
    const providerLS = window.localStorage.getItem('loginProvider')
    const existJWT = getJWTLocalStorage();
    const [searchParams] = useSearchParams();
    const errorUrl = searchParams.get('error');
    const provider = searchParams.get('provider') ?? providerLS;
    const state = searchParams.get('state') ?? existJWT;
    const isLoading = Boolean((provider && state) || existJWT);
    const [loading, setLoading] = useState<boolean>(isLoading && !errorUrl);
    const [error, setError] = useState<string | null>(errorUrl ? 'Error al iniciar sesión' : null);
    const [user, setUser] = useState<PerseaUserType | null>(null);
    const [isConnect, setIsConnect] = useState<boolean>(false);

    const [jwt, setJWT] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(existJWT ? MESSAGES.VALIDATE_SESSION : null);
    const [loginInBackground, setLoginInBackground] = useState<boolean>(false);
    const [apiError, contextHolderError] = notification.useNotification({
        maxCount: 1
    });
    const [apiLoading, contextHolderLoading] = notification.useNotification({
        maxCount: 1
    });
    const userExist = Boolean(user?.main_key && user?.username);
    const { balance, refetch: refetchBalance } = useBalance(defaultValues?.user?.balance ?? 0, userExist);

    const createNewUser = useCallback(async (publicAddress: string, email: string, magic_token: string) => {
        const newUser = await create(DOMAIN, NETWORK_NAME, {
            address: publicAddress,
            email,
            magic_token,
        });
        localStorage.setItem('isNewUser', 'true');
        return newUser;
    }, []);

    const handleExistingUser = useCallback(async (publicAddress: string, email: string | undefined, magic_token: string, type: 'MAGIC' | 'SOCIAL') => {
        if (!publicAddress && !magic_token) {
            throw new Error("Missing required auth data");
        }
        setLoginInBackground(true);
        setMessage(MESSAGES.GET_DATA);
        let recipientUser = await findByWallet(publicAddress);
        if(state && recipientUser && selectedLogin === 'OAUTH' && type === 'SOCIAL'){
            setXApiKeyLocalStorage(state);
            setJWT(state);
            setUser({...recipientUser[0], balance: recipientUser?.[0]?.balance ?? 0});
            setIsConnect(true);
            return;
        }
        if (!recipientUser || (Array.isArray(recipientUser) && recipientUser.length === 0)) {
            const emailTemp = `${publicAddress}@${DOMAIN}`;
            await createNewUser(publicAddress, email ?? emailTemp, magic_token);
            recipientUser = await findByWallet(publicAddress);
        }
        if (Array.isArray(recipientUser) && recipientUser.length > 0) {
            const jwt = await generateJwt(DOMAIN, "Login", "Login", magic_token);
            if (!jwt) {
                throw new Error("Error generating JWT");
            }
            setXApiKeyLocalStorage(jwt);
            setJWT(jwt);
            setUser({...recipientUser[0], balance: recipientUser?.[0]?.balance ?? 0});
            setIsConnect(true);
        } else {
            setUser(null);
            setIsConnect(false);
            setJWT(null);
            setLoginInBackground(false);
        }
    }, [createNewUser, state, selectedLogin]);

    const loginWithEmail = useCallback(async (email: string) => {
        try {
            setMessage(MESSAGES.LOGIN);
            setLoading(true);
            setError(null);
            const { userInfo, magic_token } = await loginEmailMagicLink(email);
            await handleExistingUser(userInfo.publicAddress, email, magic_token, 'MAGIC');
        } catch (error) {
            console.error("Login error:", error);
            setError('Ocurrió un error al iniciar sesión');
            setLoginInBackground(false);
        } finally {
            setLoading(false);
        }
    }, [handleExistingUser]);

    const loginWithSocialProvider = useCallback(async (provider: AuthProviers) => {
        try {
            setMessage(MESSAGES.LOGIN);
            setLoading(true);
            setError(null);
            window.localStorage.setItem('loginProvider', provider)
            await loginFunc.login(provider);
        } catch (error) {
            console.error(`Error logging in with ${provider}`, error);
            setError(`Error al iniciar sesión con ${provider}`);
            setLoginInBackground(false);
            window.localStorage.removeItem('loginProvider');
        } finally {
            setLoading(false);
        }

    }, [loginFunc]);

    const logout = useCallback(async () => {
        try {
            setMessage(MESSAGES.LOGOUT);
            setLoading(true);
            setLoginInBackground(false);
            setIsConnect(false);
            removeJWTLocalStorage();
            window.localStorage.removeItem('pathHistory');
            setUser(null);
            setJWT(null);
            navigate('/login');
            await loginFunc.logout();
        } catch (error) {
            console.error("Error during logout.", error);
            setError("Error al iniciar sesión.");
        } finally {
            setLoading(false);
        }
    }, [navigate, loginFunc]);

    const validateMagicSession = useCallback(async () => {
        try {
            setMessage(MESSAGES.VALIDATE_SESSION);
            setLoading(true);
            amplitudeAddTrack('Reconnection session', {
                method: 'Magic',
                timestamp: new Date().toISOString(),
            });
            const type = String(selectedLogin)?.toUpperCase()
            const { userInfo, magic_token } = await loginFunc.isLogged(state??'') as { userInfo: { publicAddress: string,  email?: string }, magic_token: string};
            await handleExistingUser(userInfo.publicAddress, userInfo.email ?? undefined, magic_token, type === 'MAGIC' ? 'MAGIC': 'SOCIAL');
        } catch (error) {
            console.error("Error validating session", error);
            setLoginInBackground(false);
            setMessage(MESSAGES.ERROR_VALIDATE_SESSION);
            removeJWTLocalStorage();
        } finally {
            setLoading(false);
        }
    }, [handleExistingUser, loginFunc, state,selectedLogin]);

    const handleGetRedirectResult = useCallback(async () => {
        try {
            setMessage(MESSAGES.LOGIN);
            setLoading(true);
            await loginFunc.reconnect();
            await validateMagicSession();
        } catch (error) {
            setLoading(false);
            setLoginInBackground(false);
            removeJWTLocalStorage();
            console.error("Error getting redirect result", error);
            setError("Error al obtener el resultado de la redirección");
        }
    }, [validateMagicSession,loginFunc]);

    const firstRender = useRef(true);
    useEffect(() => {
        if (firstRender.current && !provider && !state && !existDefaultValues && existJWT && !errorUrl) {
            firstRender.current = false;
            validateMagicSession();
        }

    }, [validateMagicSession, provider, state, existDefaultValues, existJWT, errorUrl]);

    const firstRender2 = useRef(true);
    useEffect(() => {
        if (firstRender2.current && state && !existDefaultValues) {
            firstRender2.current = false;
            handleGetRedirectResult();
        }
    }, [state, handleGetRedirectResult, existDefaultValues]);

    const openNotificationError = useCallback((placement: NotificationPlacement) => {
        apiError.error({
            message: `Ups!`,
            description: error,
            placement,
            duration: 0,
        });
    }, [error, apiError]);

    useEffect(() => {
        apiError.destroy();
        if (error) {
            openNotificationError('topRight');
        } else {
            apiError.destroy();
        }
    }, [error, apiError, openNotificationError]);


    const openNotificationLoading = useCallback((placement: NotificationPlacement) => {
        apiLoading.info({
            message: message,
            description: <div>
                <Spin indicator={<LoadingOutlined spin style={{ color: theme?.colors?.theme?.brandColor3 }} />} size="small" style={{ marginRight: '8px' }} />
                <span>Por favor espera...</span>
            </div>,
            placement,
            duration: 0,
            key: 'loading-notification'
        });
    }, [apiLoading, message, theme]);

    useEffect(() => {
        if (loading && message) {
            openNotificationLoading('topRight');
        } else {
            apiLoading.destroy();
        }
    }, [loading, apiLoading, openNotificationLoading, message]);


    useEffect(() => {
        if (balance !== user?.balance && user) {
            setUser(prev => prev ? { ...prev, balance } : null);
        }
    }, [user, balance]);


    const value = useMemo(() => ({
        logout,
        loginWithEmail,
        loginWithSocialProvider,
        user,
        setUser,
        jwt,
        error,
        loading,
        setLoading,
        refetchBalance,
        isConnect,
        loginInBackground,
    }), [user, jwt, loading, error, logout, loginWithEmail, loginWithSocialProvider, refetchBalance, isConnect, loginInBackground]);


    return (
        <AuthContext.Provider value={existDefaultValues ? defaultValuesReactive : value}>
            {notificationHolderError && <div key="error-holder">{contextHolderError}</div>}
            {notificationHolderLoading && <div key="loading-holder">{contextHolderLoading}</div>}
            {children}
        </AuthContext.Provider>
    );
};